import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Minus, Plus } from 'lucide-react';
import { Container, Eyebrow, PillButton } from './ui';

const faqs = [
  {
    q: 'How long does a typical project take?',
    a: 'It depends on scope. A business website usually takes a few weeks, while a full web or mobile application takes longer. You get a clear timeline and milestones before we begin.',
  },
  {
    q: 'What technologies do you work with?',
    a: "I choose proven, modern tools based on what your product actually needs - its goals, budget, and how it will grow - rather than forcing one setup on every project. You'll always know what's being used and why.",
  },
  {
    q: 'Do you build apps for both iOS and Android?',
    a: 'Yes. I build cross-platform mobile apps from a single codebase, so you launch on both stores faster without sacrificing a native feel.',
  },
  {
    q: 'Do you provide post-launch support?',
    a: 'Yes. After launch I handle bug fixes, monitoring, and improvements, and ongoing work can continue on a monthly retainer.',
  },
];

const FaqItem = ({ item, open, onToggle }) => (
  <div className="rounded-2xl bg-[#f4f6fa]">
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="w-full flex items-center justify-between gap-4 text-left px-5 py-5"
    >
      <span className="text-[15px] font-medium text-[#111]">{item.q}</span>
      {open ? <Minus className="w-4 h-4 shrink-0 text-[#111]" /> : <Plus className="w-4 h-4 shrink-0 text-[#111]" />}
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="overflow-hidden"
        >
          <p className="px-5 pb-5 -mt-1 text-[13px] leading-relaxed text-[#111]/65 max-w-[380px]">{item.a}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const Faq = () => {
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" className="bg-[#e9ecf2] pt-8 pb-20 lg:pb-28">
      <Container className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        <div className="rounded-3xl min-h-[260px] lg:min-h-[260px] flex flex-col items-center justify-center text-center px-6 py-12 bg-[radial-gradient(85%_95%_at_45%_40%,#3b5bfd_0%,#6f86ff_45%,#dfe4f9_85%,#eef1fb_100%)] shadow-[0_20px_50px_rgba(59,91,253,0.15)]">
          <Eyebrow dark starClassName="text-white">FAQ Questions</Eyebrow>
          <h2 className="mt-3 text-[30px] sm:text-[36px] font-medium leading-[1.1] tracking-[-0.04em] text-white">
            Got questions about
            <br />
            working together?
          </h2>
          <PillButton to="/contact" className="mt-7">
            Get In Touch
          </PillButton>
        </div>

        <div className="space-y-2.5">
          {faqs.map((item, i) => (
            <FaqItem key={item.q} item={item} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
          ))}
        </div>
      </Container>
    </section>
  );
};

export default Faq;
